import { ClassifierData } from 'types/classifiers';
import { AddressDto } from 'types/contacts';
import { SelectLabelType, SelectType, SelectValueType } from 'types/forms';
import { isHiddenClassifierValue } from 'utils/classifierUtil';
import { getClassifierText, getClassifierTextDescription } from './getTranslations';

export const getOptionsFromClassifier = <L extends SelectLabelType, V extends SelectValueType>(
    classifier: ClassifierData | undefined
): SelectType<L, V>[] => {
    if (!classifier) {
        return [];
    }

    return classifier.values
        ? classifier.values
              .map((type) => ({
                  label: getClassifierText(classifier, type.code),
                  value: type.code,
                  description: getClassifierTextDescription(classifier, type.code),
                  parameters: type.parameters,
                  orderNum: type.orderNum,
              }))
              .sort((a, b) => {
                  // Sort by orderNum first (nulls last), then by label
                  if (a.orderNum != null && b.orderNum != null) {
                      return a.orderNum - b.orderNum;
                  }
                  if (a.orderNum != null) {
                      return -1;
                  }
                  if (b.orderNum != null) {
                      return 1;
                  }
                  return a.label.localeCompare(b.label);
              })
              .map((a) => selectTypeTypeMapper<L, V>(a))
        : [];
};

export const getValidOptionsFromClassifier = <L extends SelectLabelType, V extends SelectValueType>(
    classifier: ClassifierData | undefined,
    sorted: boolean = true
): SelectType<L, V>[] => {
    if (!classifier || !classifier.values) {
        return [];
    }

    const now = new Date();

    const toReturn = classifier.values
        ? classifier.values
              .filter((type) => {
                  return (
                      !type.deletedAt &&
                      (!type.validFrom || new Date(type.validFrom) < now) &&
                      (!type.validUntil || new Date(type.validUntil) > now)
                  );
              })
              .filter((type) => !isHiddenClassifierValue(classifier.code, type.code))
              .map((type) => ({
                  label: getClassifierText(classifier, type.code),
                  value: type.code,
                  description: getClassifierTextDescription(classifier, type.code),
                  parameters: type.parameters,
                  orderNum: type.orderNum,
              }))
              .sort((a, b) => {
                  if (sorted === false) {
                      return 0;
                  }
                  // Sort by orderNum first (nulls last), then by label
                  if (a.orderNum != null && b.orderNum != null) {
                      return a.orderNum - b.orderNum;
                  }
                  if (a.orderNum != null) {
                      return -1;
                  }
                  if (b.orderNum != null) {
                      return 1;
                  }
                  return a.label.localeCompare(b.label);
              })
              .map((a) => selectTypeTypeMapper<L, V>(a))
        : [];
    return toReturn;
};

/**
 * Resolves a code a record already holds to its option, ignoring every "may I still pick this"
 * filter — the validity window, the hidden-value list, and soft delete alike.
 *
 * Use this whenever an existing value is being restored into a form, and `getValidOptionsFromClassifier`
 * for the options that form then offers. The two are genuinely different questions: a value that must
 * not be offered still has to display on the records that already store it, which is exactly why the
 * backend keeps returning soft-deleted values instead of hiding them.
 */
export const getClassifierOptionByCode = <L extends SelectLabelType, V extends SelectValueType>(
    classifier: ClassifierData | undefined,
    code: SelectValueType | null
): SelectType<L, V> | null => {
    if (code === null || code === undefined) {
        return null;
    }

    return getOptionsFromClassifier<L, V>(classifier).find((option) => option.value === code) ?? null;
};

export const getSortedOptions = <T>(
    getLabel: Extract<keyof T, string> | ((item: T) => string),
    getValue: Extract<keyof T, string> | ((item: T) => string),
    optionElements: T[]
): SelectType[] => {
    return optionElements
        .reduce<SelectType[]>((currentArray, currentItem) => {
            const label = (typeof getLabel === 'string' ? currentItem[getLabel] : getLabel(currentItem)) as string;
            const value = (typeof getValue === 'string' ? currentItem[getValue] : getValue(currentItem)) as string;
            if (label && value) {
                currentArray.push({ label, value });
            }
            return currentArray;
        }, [])
        .sort((a, b) => (a.label as string).localeCompare(b.label as string));
};

export const getAddress = (address: AddressDto | null, countyLabel?: string): string => {
    if (address === null) return '';

    const filteredCustomFields = address.customFields?.filter((customField) =>
        customField.isMultivalue ? customField.values : customField.value
    );

    const hasCustomFieldsWithValue = filteredCustomFields?.length;

    return `${address?.address1 ?? ''} 
    ${address?.address2 ?? ''} 
    ${address?.cityName ?? ''}, 
    ${address?.zipCode ?? ''} 
    ${countyLabel ?? address?.countryCode ?? ''}${hasCustomFieldsWithValue ? ',' : ''}
    ${
        address.customFields && hasCustomFieldsWithValue
            ? filteredCustomFields?.map((field) => (field.isMultivalue ? field.values : field.value)).join(', ')
            : ''
    }`;
};

const selectTypeTypeMapper = <L extends SelectLabelType, V extends SelectValueType>(
    type: SelectType
): SelectType<L, V> => ({
    label: type.label as L,
    value: type.value as V,
    group: type.group,
    parameters: type.parameters,
});

export const getLabelWithDescription =
    <L extends SelectLabelType, V extends SelectValueType>(classifierData: ClassifierData | undefined) =>
    (option: SelectType<L, V>): string => {
        if (!option || !option.value) return String(option?.label || '');

        const description = getClassifierTextDescription(classifierData, String(option.value));
        if (description && description.trim() && description !== String(option.value)) {
            return `${option.label} (${description})`;
        }

        return String(option.label);
    };
